<template>
    <div>
        <div class="mb-6 p-8 h-48 w-full border border-light-gray rounded lg:h-72">
            <img :src="imagePath"
                 :alt="imageAlt"
                 class="h-full w-full object-contain">
        </div>
        <h3 class="mb-4 normal-case text-2xl lg:text-3xl">{{ title }}</h3>
        <h4 class="mb-2 text-base lg:text-lg normal-case">{{ subtitle }}</h4>
        <p class="text-dark-gray">{{ description }}</p>
        <div class="mt-4 lg:mt-6 grid grid-cols-6 gap-x-6">
            <label class="col-span-4 flex flex-col">
                <div class="mb-2 text-base lg:text-lg">{{ weightLabel }}</div>
                <form-dropdown @selected="checkCustomValidity" ref="option" class="col-span-4" :name="weightDropdownName" :label="weightSelectLabel"
                               :items="weightOptions" allow-deselect></form-dropdown>
            </label>
            <label class="col-span-2 flex flex-col">
                <div class="mb-2 text-base lg:text-lg">{{ amountLabel }}</div>
                <input @input='checkCustomValidity'
                       v-model.number="amount"
                       type="number"
                       min="0"
                       max="10"
                       placeholder="0"
                       value="0"
                       :name="amountName"
                       class="p-2 border border-gray focus:border-fruechtebox-orange outline-none rounded"
                       :class="[amount > 0 ? 'border-opacity-100' : 'border-opacity-20']"
                >
            </label>
        </div>
        <div v-if="subscription">
            <label class="mt-4 flex flex-col">
                <div class="mb-2 text-base lg:text-lg">{{ subscriptionLabel }}</div>
                <form-dropdown @selected="checkCustomValidity" ref="subscription" class="col-span-4" :name="subscriptionDropdownName" :label="subscriptionSelectLabel"
                               :items="subscriptionOptions" allow-deselect></form-dropdown>
            </label>
        </div>
        <div class="mt-4 text-fruechtebox-orange" v-if="validityState > 1">
            {{ selectionError }}
        </div>
    </div>
</template>

<script lang="ts">
import { PropType } from 'vue';

interface DropdownOption {
    label: string;
    value: string;
}

export enum ValidityState {
    NONE_SELECTED,
    SELECTED,
    PARTIALLY_SELECTED,
}
export default {
    data() {
        return {
            validityState: ValidityState.NONE_SELECTED,
            amount: 0
        };
    },
    props: {
        subscription: {
            type: Boolean,
            default: false
        },
        imagePath: {
            type: String,
            required: true
        },
        imageAlt: {
            type: String,
            required: true
        },
        title: {
            type: String,
            required: true
        },
        subtitle: {
            type: String,
            required: true
        },
        description: {
            type: String,
            required: true
        },
        weightDropdownName: {
            type: String,
            required: true
        },
        weightLabel: {
            type: String,
            required: true
        },
        weightSelectLabel: {
            type: String,
            required: true
        },
        weightOptions: {
            type: Array as PropType<DropdownOption[]>,
            required: true
        },
        amountLabel: {
            type: String,
            required: true
        },
        amountName: {
            type: String,
            required: true
        },
        subscriptionDropdownName: {
            type: String
        },
        subscriptionLabel: {
            type: String
        },
        subscriptionSelectLabel: {
            type: String
        },
        subscriptionOptions: {
            type: Array as PropType<DropdownOption[]>
        },
        selectionError: {
            type: String,
            required: true
        }
    },
    methods: {
        checkCustomValidity(): boolean {
            const optionValueLength = this.$refs.option.value.length;
            const subscriptionValueLength = this.subscription ? this.$refs.subscription.value.length : 0;
            if (optionValueLength === 0 && this.amount === 0 && subscriptionValueLength === 0) {
                this.validityState = ValidityState.NONE_SELECTED;
            } else if (optionValueLength > 0 && this.amount > 0 && (this.subscription ? subscriptionValueLength > 0 : true)) {
                this.validityState = ValidityState.SELECTED;
            } else {
                this.validityState = ValidityState.PARTIALLY_SELECTED;
            }
            return this.validityState < 2;
        },
        reportCustomValidity() {
            this.checkCustomValidity();
        }
    },
    inject: ['registerProduct'],
    mounted() {
        this.registerProduct(this);
    }
};

</script>
